import { useCallback, useEffect, useRef, useState } from 'react';
import { getZooConfig } from '../lib/auth/onboarding';
import { getSupabase } from '../lib/supabase/client';

/**
 * Staging-only floating panel for quick user switching via server-side impersonation.
 *
 * Fetches profiles with special roles, then calls the `staging-impersonate`
 * Edge Function which generates a magic link token (no password in frontend).
 * Only renders when SUPABASE_FORCE=true (build-time — dead-code eliminated in production).
 */

interface StagingUser {
  id: string;
  fullName: string;
  email: string;
  roles: string;
}

export function StagingUserSwitcher() {
  // Build-time constant — RSBuild replaces with '' in production → dead-code eliminated
  if (process.env.SUPABASE_FORCE !== 'true') return null;

  return <StagingUserSwitcherInner />;
}

function StagingUserSwitcherInner() {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<StagingUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Current user ID from Supabase session
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    const client = getSupabase();
    if (!client) return;
    client.auth.getSession().then(({ data }) => {
      setCurrentUserId(data.session?.user?.id ?? null);
    });
  }, []);

  // Fetch users when panel opens
  useEffect(() => {
    if (!open || users.length > 0) return;

    setLoading(true);
    setError(null);

    const client = getSupabase();
    if (!client) {
      setError('Supabase not configured');
      setLoading(false);
      return;
    }

    client
      .from('profiles')
      .select(`
        id,
        full_name,
        email,
        user_special_roles (
          special_roles (
            name
          )
        )
      `)
      .is('archived_at', null)
      .order('full_name')
      .then(({ data, error: queryError }) => {
        if (queryError) {
          setError(queryError.message);
          setLoading(false);
          return;
        }

        const mapped: StagingUser[] = (data ?? []).map((p: any) => {
          const roleNames = (p.user_special_roles ?? [])
            .map((usr: any) => usr.special_roles?.name)
            .filter(Boolean);

          return {
            id: p.id,
            fullName: p.full_name ?? p.email,
            email: p.email,
            roles: roleNames.join(', '),
          };
        });

        setUsers(mapped);
        setLoading(false);
      });
  }, [open, users.length]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  const handleSwitch = useCallback(async (userId: string) => {
    setSwitching(userId);
    setError(null);

    const client = getSupabase();
    if (!client) {
      setError('Supabase not configured');
      setSwitching(null);
      return;
    }

    // Get current session for auth header
    const { data: sessionData } = await client.auth.getSession();
    if (!sessionData?.session) {
      setError('Not authenticated');
      setSwitching(null);
      return;
    }

    const config = getZooConfig();
    if (!config) {
      setError('Zoo not configured');
      setSwitching(null);
      return;
    }

    // Call staging-impersonate Edge Function
    try {
      const response = await fetch(
        `${config.supabaseUrl}/functions/v1/staging-impersonate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionData.session.access_token}`,
            apikey: config.supabaseAnonKey,
          },
          body: JSON.stringify({ target_user_id: userId }),
        },
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setError((body as any).error ?? `Impersonation failed (${response.status})`);
        setSwitching(null);
        return;
      }

      const { hashed_token, verification_type } = await response.json();

      // Exchange token for a real session
      const { error: otpError } = await client.auth.verifyOtp({
        token_hash: hashed_token,
        type: verification_type,
      });

      if (otpError) {
        setError(otpError.message);
        setSwitching(null);
        return;
      }

      // Clean reload to reset all state (urql cache, component state, pgSettings)
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
      setSwitching(null);
    }
  }, []);

  return (
    <div ref={panelRef} style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>
      {/* Expanded panel */}
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: 56,
            right: 0,
            width: 300,
            maxHeight: 400,
            backgroundColor: 'var(--color-bg-primary, #fff)',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            border: '1px solid var(--color-border-opaque, #e0e0e0)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-border-opaque, #e0e0e0)',
              backgroundColor: 'var(--color-bg-secondary, #f8f8f8)',
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--color-label-primary, #000)',
                fontFamily: 'var(--font-family-primary)',
              }}
            >
              Switch User
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--color-label-tertiary, #999)',
                fontFamily: 'var(--font-family-primary)',
                marginTop: 2,
              }}
            >
              Staging — server-side impersonation
            </div>
          </div>

          {/* User list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading && (
              <div
                style={{
                  padding: 24,
                  textAlign: 'center',
                  fontSize: 13,
                  color: 'var(--color-label-tertiary, #999)',
                  fontFamily: 'var(--font-family-primary)',
                }}
              >
                Loading users...
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: '12px 16px',
                  fontSize: 12,
                  color: 'var(--color-tint-red, #e53e3e)',
                  fontFamily: 'var(--font-family-primary)',
                }}
              >
                {error}
              </div>
            )}

            {!loading && users.length === 0 && !error && (
              <div
                style={{
                  padding: 24,
                  textAlign: 'center',
                  fontSize: 13,
                  color: 'var(--color-label-tertiary, #999)',
                  fontFamily: 'var(--font-family-primary)',
                }}
              >
                No users found
              </div>
            )}

            {users.map((user) => {
              const isCurrent = user.id === currentUserId;
              const isSwitching = switching === user.id;

              return (
                <button
                  type="button"
                  key={user.id}
                  onClick={() => !isCurrent && !switching && handleSwitch(user.id)}
                  disabled={isCurrent || !!switching}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                    padding: '10px 16px',
                    border: 'none',
                    borderBottom: '1px solid var(--color-border-opaque, #e0e0e0)',
                    backgroundColor: isCurrent
                      ? 'var(--color-tint-light-blue, #e8f4fd)'
                      : 'transparent',
                    cursor: isCurrent || switching ? 'default' : 'pointer',
                    textAlign: 'left',
                    fontFamily: 'var(--font-family-primary)',
                    transition: 'background-color 0.1s ease',
                    opacity: switching && !isSwitching ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrent && !switching) {
                      e.currentTarget.style.backgroundColor =
                        'var(--color-bg-secondary, #f5f5f5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: isCurrent ? 600 : 400,
                        color: 'var(--color-label-primary, #000)',
                      }}
                    >
                      {isSwitching ? 'Switching...' : user.fullName}
                    </span>
                    {isCurrent && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--color-tint-blue, #007aff)',
                          backgroundColor: 'var(--color-tint-light-blue, #e8f4fd)',
                          padding: '1px 6px',
                          borderRadius: 4,
                        }}
                      >
                        CURRENT
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--color-label-tertiary, #999)',
                    }}
                  >
                    {user.email}
                    {user.roles ? ` — ${user.roles}` : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* FAB button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Switch staging user"
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          border: 'none',
          backgroundColor: 'var(--color-tint-orange, #ff9500)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          position: 'relative',
          transition: 'transform 0.15s ease',
          transform: open ? 'scale(0.95)' : 'scale(1)',
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <polyline points="16 11 18 13 22 9" />
        </svg>

        <span
          style={{
            position: 'absolute',
            top: -6,
            right: -8,
            fontSize: 8,
            fontWeight: 700,
            color: '#fff',
            backgroundColor: 'var(--color-tint-red, #e53e3e)',
            padding: '1px 5px',
            borderRadius: 6,
            fontFamily: 'var(--font-family-primary)',
            letterSpacing: 0.5,
            lineHeight: '14px',
          }}
        >
          STG
        </span>
      </button>
    </div>
  );
}
